import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import dayjs from 'dayjs';
import { groupBy } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';
import { SaveChangesComponent } from '@features/save-changes/save-changes.component';
import { WorkflowPanelComponent } from '@features/workflow-panel/workflow-panel.component';

import { PatientCurveQuery } from '@models/patient-curve/patient-curve.query';
import { PatientCurveService } from '@models/patient-curve/patient-curve.service';
import { PatientGroupsService } from '@models/patient-group/patient-groups.service';
import { SiteCurveQuery } from '@models/site-curve/site-curve.query';

import { ButtonComponent } from '@shared/components/button/button.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { VendorDropdownComponent } from '@shared/components/vendor-dropdown/vendor-dropdown.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { NoopValueAccessorDirective } from '@shared/directives/noop-value-accessor.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { MoneyPipe } from '@shared/pipes/money.pipe';
import {
  BudgetType,
  Currency,
  DriverType,
  EntityType,
  EventType,
  GqlService,
  PermissionType,
  WorkflowStep,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventQuery } from '@shared/store/event/event.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationStore } from '@shared/store/organization/organization.store';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { ExportType } from '@shared/utils/utils';

import { ForecastAnalyticsSectionComponent } from './components/forecast-analytics-section/forecast-analytics-section.component';
import { ForecastTableGridComponent } from './components/forecast-table/forecast-table-grid.component';
import { ForecastTableGridDataInterface } from './components/forecast-table/models/forecast-table-grid.model';
import { QuickForecastModalComponent } from './components/quick-forecast-modal/quick-forecast-modal.component';
import { ActivityQuery } from './state/activity/activity.query';
import { CategoryQuery } from './state/category/category.query';
import { CategoryService } from './state/category/category.service';
import { CategoryStore } from './state/category/category.store';
import { ForecastSettingsQuery } from './state/settings/forecast-settings.query';
import { ForecastSettingsStore } from './state/settings/forecast-settings.store';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    WorkflowPanelComponent,
    NoopValueAccessorDirective,
    VendorDropdownComponent,
    FormsModule,
    ReactiveFormsModule,
    NgTemplateOutlet,
    IconComponent,
    ForecastAnalyticsSectionComponent,
    RouterLink,
    CheckboxComponent,
    ButtonComponent,
    TooltipDirective,
    ExportExcelButtonComponent,
    ForecastTableGridComponent,
    NgClass,
    SaveChangesComponent,
    MoneyPipe,
    AsyncPipe,
  ],
  selector: 'aux-forecast',
  styles: [
    `
      ::ng-deep .ng-select.ng-select-disabled .ng-arrow-wrapper {
        display: none;
      }

      ::ng-deep .ng-select.ng-select-disabled > .ng-select-container {
        background-color: var(--aux-gray-light);
      }

      ::ng-deep .ng-select.ng-select-disabled .ng-value {
        color: var(--aux-gray-dark-100);
      }
    `,
  ],
  templateUrl: './forecast.component.html',
})
export class ForecastComponent implements OnInit {
  categoryQuery = inject(CategoryQuery);
  categoryStore = inject(CategoryStore);
  organizationQuery = inject(OrganizationQuery);

  private activityQuery = inject(ActivityQuery);
  private authQuery = inject(AuthQuery);
  private authService = inject(AuthService);
  private categoryService = inject(CategoryService);
  private destroyRef = inject(DestroyRef);
  private eventQuery = inject(EventQuery);
  private eventService = inject(EventService);
  private forecastSettingsQuery = inject(ForecastSettingsQuery);
  private forecastSettingsStore = inject(ForecastSettingsStore);
  private gqlService = inject(GqlService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private organizationService = inject(OrganizationService);
  private organizationStore = inject(OrganizationStore);
  private overlayService = inject(OverlayService);
  private patientCurveQuery = inject(PatientCurveQuery);
  private patientCurveService = inject(PatientCurveService);
  private patientGroupsService = inject(PatientGroupsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private siteCurveQuery = inject(SiteCurveQuery);
  private workflowQuery = inject(WorkflowQuery);

  hasUnforecastedCustomDrivers = computed(() => {
    const settings = this.forecastSettingsQuery.settings() || [];

    const unforecastedCustomDrivers = settings.filter(
      (setting) => setting.driver === DriverType.DRIVER_CUSTOM && !setting.driver_setting_id
    );

    return !!unforecastedCustomDrivers.length;
  });

  workflowName = WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_FORECAST_METHODOLOGY;

  lockForecastMethodologyTooltip = computed(() => {
    const isFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(this.workflowName)();

    return isFinalized ? MessagesConstants.PAGE_LOCKED_FOR_PERIOD_CLOSE : '';
  });

  hasModifyForecastMethodologyPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_MODIFY_FORECAST_METHODOLOGY],
    sysAdminsOnly: false,
  });

  noModifyForecastMethodologyPermissionTooltip = computed(() => {
    return this.hasModifyForecastMethodologyPermission()
      ? ''
      : MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
  });
  saveChangesTooltip = computed(() => {
    if (this.hasUnforecastedCustomDrivers()) {
      return 'Select a Custom Curve in Method dropdown to Save Changes.';
    }

    return '';
  });

  btnLoading$ = new BehaviorSubject<'export' | false>(false);
  categoryTypes: string[] = [];
  changeDiscountTotalAmount = new UntypedFormControl(0);
  isForecastFinalized$ = this.workflowQuery.getLockStatusByWorkflowStepType$(this.workflowName);
  changeDiscountTotalPercent = new UntypedFormControl({
    disabled: this.isForecastFinalized$,
    value: 0,
  });
  discountType$ = new BehaviorSubject('DISCOUNT_PERCENTAGE');
  @ViewChild(ForecastTableGridComponent) forecastTableGridComponent!: ForecastTableGridComponent;
  hideDiscounts$ = new BehaviorSubject(true);
  iCloseMonthsProcessing$ = this.eventQuery.selectProcessingEvent$(EventType.CLOSE_TRIAL_MONTH);
  isAdminUser = false;
  isClosingPanelEnabled$ = this.launchDarklyService.select$(
    (flags) => flags.closing_checklist_toolbar
  );
  isPatientDriverAvailable = false;
  isPatientDriversLinkVisible = false;
  isQuarterCloseEnabled$ = this.workflowQuery.isWorkflowAvailable$;
  isQuickForecastProcessing = this.eventQuery.selectProcessingEvent(
    EventType.BUDGET_FORECAST_SETTINGS_UPDATED
  );
  orgCurrency = Currency.USD;
  isSelectedVendorHasManualForecast$ = this.organizationQuery.selectActive().pipe(
    map((org) => {
      this.orgCurrency = org?.currency || Currency.USD;

      return !!this.organizationQuery.getPrimaryBudgetVersion(org?.id || '')?.manual_forecast;
    })
  );
  isSiteDriverAvailable = false;
  isSiteDriversLinkVisible = false;
  patientDriversLink = `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.CURVES}`;
  saveCheck$ = new BehaviorSubject(false);
  selectedVendor = new UntypedFormControl('');
  showAnalyticsSection$: Observable<boolean> = this.launchDarklyService.select$(
    (flags) => flags.section_forecast_analytics
  );
  siteDriversLink = `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.SITE_DRIVER.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.SITE_DRIVER.CURVES}`;
  successForecast$ = new BehaviorSubject(true);
  topLevelActivities$ = this.activityQuery.selectAll({
    filterBy: (a) => {
      return !a.category_id;
    },
  });
  userHasLockForecastMethodologyPermission = false;

  ngOnInit(): void {
    this.patientGroupsService.get().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.patientCurveService.get().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.categoryService.getCategories().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.authQuery.adminUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isAdminUser) => {
      this.isAdminUser = isAdminUser;
    });

    this.initVendorFilter().subscribe(([{ vendor_id }]) => {
      this.processVendorFilter(vendor_id);
    });

    this.initForecastChanges().subscribe(([isFinalized, iCloseMonthsProcessing]) => {
      this.processForecastChanges(isFinalized, iCloseMonthsProcessing);
    });

    this.initTrialKey().subscribe();

    this.initTrialChanges().subscribe(() => {
      this.processTrialChanges();
    });

    let selectedVendorId = localStorage.getItem('forecastVendor');

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (params.vendor) {
        selectedVendorId = params.vendor;
        void this.router.navigate([], { queryParams: {}, replaceUrl: true });
      }
    });

    if (selectedVendorId) {
      this.setVendorId(selectedVendorId);
    }
  }

  beforeSelectVendor = () => {
    return this.canDeactivate();
  };

  async canDeactivate(modalData?: { goNextText: string; text: string }): Promise<boolean> {
    if (this.saveCheck$.getValue()) {
      const result = this.overlayService.openUnsavedChangesConfirmation(modalData ?? undefined);
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  clearFilters() {
    this.categoryStore.hideActivitiesWithNoRemainingCost$.next(false);
    this.categoryStore.hideForecastedActivities$.next(false);
  }

  constructor() {
    this.setUserPermissions();

    effect(() => {
      const isProcessing = this.isQuickForecastProcessing();

      // fetch new categories after event finished
      if (isProcessing === false) {
        void firstValueFrom(this.categoryService.getCategories());
      }
    });
  }

  initForecastChanges() {
    return combineLatest([this.isForecastFinalized$, this.iCloseMonthsProcessing$]).pipe(
      takeUntilDestroyed(this.destroyRef)
    );
  }

  initTrialChanges() {
    return combineLatest([
      this.eventService.select$(EventType.TRIAL_CHANGED),
      this.mainQuery.select('trialKey'),
    ]).pipe(takeUntilDestroyed(this.destroyRef));
  }

  initTrialKey() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.hideDiscounts$.next(true);
        this.forecastSettingsStore.ui.update(() => ({ showError: false }));

        return combineLatest([
          this.patientCurveQuery.selectAll(),
          this.siteCurveQuery.selectAll(),
        ]).pipe(
          tap(([patientCurveResults, siteCurveResults]) => {
            this.isPatientDriverAvailable = !!patientCurveResults?.length;
            this.isSiteDriverAvailable = !!siteCurveResults?.length;
          })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  initVendorFilter() {
    return combineLatest([this.route.queryParams, this.organizationService.get()]).pipe(
      takeUntilDestroyed(this.destroyRef)
    );
  }

  isBtnLoading(str: string) {
    return this.btnLoading$.pipe(map((x) => x === str));
  }

  navigateToBudgetForecast() {
    void this.router.navigate([ROUTING_PATH.BUDGET.INDEX, ROUTING_PATH.BUDGET.INDEX], {
      queryParams: { scrollTo: 'forecast', vendorId: this.selectedVendor.value },
    });
  }

  onChangeDiscountTotalAmount(newValue: number): void {
    this.changeDiscountTotalAmount.setValue(newValue);
  }

  onChangeDiscountTotalAmountEnable(enable: boolean): void {
    if (enable) {
      this.changeDiscountTotalAmount.enable();
    } else {
      this.changeDiscountTotalAmount.disable();
    }
  }

  onChangeDiscountTotalPercent(newValue: number): void {
    this.changeDiscountTotalPercent.setValue(newValue);
  }

  onChangeDiscountTotalPercentEnable(enable: boolean): void {
    if (enable) {
      this.changeDiscountTotalPercent.enable();
    } else {
      this.changeDiscountTotalPercent.disable();
    }
  }

  async onDownloadForecastSettings() {
    const trialName = this.mainQuery.getSelectedTrial()?.short_name || '';
    const dateStr = dayjs(new Date()).format('YYYY.MM.DD-HHmmss');

    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next('export');

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_type: ExportType.FORECAST_SETTINGS,
          filename: `${trialName}_Forecast Methodology_${dateStr}`,
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }

    this.btnLoading$.next(false);
  }

  onGridValueChange(value: ForecastTableGridDataInterface[]) {
    const groupedValues = groupBy(value, 'costCategoryType');

    this.categoryTypes = Object.keys(groupedValues);
  }

  onHideActivitiesWithNoRemainingCost(bool: boolean) {
    this.categoryStore.hideActivitiesWithNoRemainingCost$.next(bool);
  }

  onHideDiscounts(newValue: boolean): void {
    this.hideDiscounts$.next(newValue);
  }

  onIsPatientDriversLinkVisible(newValue: boolean): void {
    this.isPatientDriversLinkVisible = newValue;
  }

  onIsSiteDriversLinkVisible(newValue: boolean): void {
    this.isSiteDriversLinkVisible = newValue;
  }

  onOrganizationSelected(orgId: string) {
    this.organizationStore.setActive(orgId);
    this.saveCheck$.next(false);
    this.forecastTableGridComponent.initialSettings = {};
    this.forecastTableGridComponent.toggleService.reset();
    this.processVendorFilter(orgId);
  }

  onSaveCheck(newValue: boolean): void {
    this.saveCheck$.next(newValue);
  }

  onShowOnlyUnforecasted(bool: boolean) {
    this.categoryStore.hideForecastedActivities$.next(bool);
  }

  onSuccessForecast(newValue: boolean): void {
    this.successForecast$.next(newValue);
  }

  async openQuickForecastModal() {
    const canDeactivate = await this.canDeactivate({
      goNextText: 'Discard Changes',
      text: 'Are you sure that you want to discard these changes?',
    });

    if (!canDeactivate) {
      return;
    }

    this.forecastTableGridComponent.onDiscardChanges();

    const vendorId = this.organizationQuery.isVendorHasBudget(this.selectedVendor.value)
      ? this.selectedVendor.value
      : null;

    this.overlayService.openPopup({
      data: {
        categoryTypes: this.categoryTypes,
        customCurves: this.forecastTableGridComponent.customCurves(),
        vendorId,
      },
      modal: QuickForecastModalComponent,
      settings: {
        header: 'Apply Drivers in Bulk',
        panelClasses: ['w-[569px]'],
        preventAutoCloseModal: true,
      },
    });
  }

  processForecastChanges(isFinalized: boolean, iCloseMonthsProcessing: boolean | null): void {
    if (isFinalized || iCloseMonthsProcessing) {
      this.changeDiscountTotalPercent.disable();
    } else {
      this.changeDiscountTotalPercent.enable();
    }
  }

  processTrialChanges(): void {
    this.saveCheck$.next(false);
    this.forecastTableGridComponent.initialSettings = {};
    this.forecastTableGridComponent.toggleService.reset();
  }

  processVendorFilter(vendorId: string): void {
    const orgId = this.organizationQuery.getActive()?.id;

    if (orgId) {
      this.setVendorId(orgId);
    } else {
      const vendors = this.organizationQuery.getAllVendors();

      if (vendors.length) {
        const vendorFromQueryUrl = vendors.find(({ id }) => id === vendorId);

        this.setVendorId(vendorFromQueryUrl?.id || vendors[0].id);
      }
    }
  }

  async setDiscountType(vendorId: string) {
    const primary = await firstValueFrom(
      this.gqlService.listBudgetVersions$([BudgetType.BUDGET_PRIMARY], vendorId)
    );

    this.discountType$.next((primary.data || [])?.[0]?.discount_type || '');
  }

  private setUserPermissions(): void {
    combineLatest([
      this.authService.isAuthorized$({
        permissions: [PermissionType.PERMISSION_CHECKLIST_FORECAST_METHODOLOGY],
        sysAdminsOnly: false,
      }),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([userHasLockForecastMethodologyPermission]) => {
        this.userHasLockForecastMethodologyPermission = userHasLockForecastMethodologyPermission;
      });
  }

  setVendorId(vendorId: string) {
    this.selectedVendor.setValue(vendorId);
    this.organizationStore.setActive(vendorId);
    void this.setDiscountType(vendorId);
    localStorage.setItem(`forecastVendor`, vendorId);
  }
}
